import { resources } from "@constants/hardcoded-strings";
import { LANGUAGE_SELECT_OPTIONS } from "@constants/language-select-options";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ThemingFormSection } from "@containers/common/theming-form-section/theming-form-section";
import { AddEmailTemplateLanguageModal } from "@containers/modals/add-email-template-language-modal/add-email-template-language-modal";
import { useModalContext } from "@context/use-modal-context";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ModalTypes } from "@enums/modal-types";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { EmailTemplateVariablesValidationErrors } from "@idw-types/common/validation/email-template/email-template-validation-errors";
import { FormattedEmailTemplate } from "@idw-types/data/email-template";
import { getEmailTemplateVariables } from "@utils/email-template/email-template-util";
import { convertDotSeparatedStringToJSONObject } from "@utils/string-util";
import merge from "lodash/merge";
import { ChangeEvent, Fragment, useMemo } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import commonStyles from "../../email-template-details-component.less";
import { EMAIL_TEMPLATE_SUBJECT_VARIABLE_NAME } from "@constants/email-template-constants";

type EmailTemplateVariablesFormSectionProps = {
    state: FormattedEmailTemplate;
    isDisabled: boolean;
    labelWidth: LabelWidthTypes;
    selectedLanguage: LocaleTypes;
    validationErrors: EmailTemplateVariablesValidationErrors;
    setSelectedLanguage: (language: LocaleTypes) => void;
    setEmailTemplateState: (state: FormattedEmailTemplate) => void;
};

export const EmailTemplateVariablesFormSection = (props: EmailTemplateVariablesFormSectionProps) => {
    const {
        state,
        labelWidth,
        isDisabled,
        selectedLanguage,
        validationErrors,
        setSelectedLanguage,
        setEmailTemplateState
    } = props;

    const resource = resources.pages.emailTemplateDetails.tabs.variables;

    const modalContext = useModalContext();

    const {
        selectedLanguages,
        availableLanguages
    } = useMemo(() => {
        const selectedLanguages = Object.keys(state.languages) as Array<LocaleTypes>;
        const availableLanguages = LANGUAGE_SELECT_OPTIONS.filter(lso => !selectedLanguages.includes(lso.value));

        return {
            selectedLanguages,
            availableLanguages
        };
    }, [state.languages]);

    const variables = useMemo(() => {
        return getEmailTemplateVariables(state.languages, state.template, selectedLanguage);
    }, [state.languages, state.template, selectedLanguage]);

    const handleUpdateLanguage = (newOption: SelectOption<LocaleTypes>) => {
        setSelectedLanguage(newOption.value);
    };

    const handleUpdateEmailSubject = (event: ChangeEvent<HTMLInputElement>) => {
        const newJSONObject = convertDotSeparatedStringToJSONObject(EMAIL_TEMPLATE_SUBJECT_VARIABLE_NAME, event.target.value);

        const newLanguageFile = merge({}, state.languages[selectedLanguage], newJSONObject);
        setEmailTemplateState({
            ...state,
            languages: {
                ...state.languages,
                [selectedLanguage]: newLanguageFile
            }
        });
    };

    const handleChangeAtIndex = (event: ChangeEvent<HTMLInputElement>, index: number) => {
        const newJSONObject = convertDotSeparatedStringToJSONObject(variables[index].name, event.target.value);
        const newLanguageFile = merge({}, state.languages[selectedLanguage], newJSONObject);

        setEmailTemplateState({
            ...state,
            languages: {
                ...state.languages,
                [selectedLanguage]: newLanguageFile
            }
        });
    };


    const onClickContinue = (localeType: LocaleTypes) => {
        setEmailTemplateState({
            ...state,
            languages: {
                ...state.languages,
                [localeType]: state.languages[LocaleTypes.ENGLISH]
            }
        });
        setSelectedLanguage(localeType);
    };

    const onClickAddLanguage = () => {
        const renderAddEmailTemplateLanguageModal = (onModalClose: () => void) => {
            return (
                <AddEmailTemplateLanguageModal
                    availableLanguages={availableLanguages}
                    onClickContinue={onClickContinue}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.AddEmailTemplateLanguage,
            renderFunction: renderAddEmailTemplateLanguageModal
        });
    };

    const languageOptions = useMemo(() => {
        return LANGUAGE_SELECT_OPTIONS.filter(lso => selectedLanguages.includes(lso.value));
    }, [state.languages, selectedLanguages]);

    const selectedLanguageOption = useMemo(() => {
        return LANGUAGE_SELECT_OPTIONS.find(lso => lso.value === selectedLanguage) || null;
    }, [state.languages, selectedLanguage]);

    const {
        languageValidationErrors
    }: { languageValidationErrors: { [id: string]: React.ReactNode }; count: number; } = useMemo(() => {
        let errors = {};
        let count = 0;
        if (validationErrors?.multiLanguageValidationErrors[selectedLanguage]) {
            const data = validationErrors.multiLanguageValidationErrors[selectedLanguage];
            errors = data.errors || {};
            count = data.count;
        }
        return {
            languageValidationErrors: errors,
            count
        };
    }, [validationErrors, selectedLanguage]);


    return (
        <ThemingFormSection title={resource.sections.variables.title}>
            <div className={commonStyles.sectionDescription}>{resource.sections.variables.description}</div>
            <SelectFormGroup
                controlId={`email-template-language-select`}
                automationId={`email-template-language-select`}
                ariaLabel={resource.inputs.language.label}
                disabled={false}
                isClearable={false}
                isRequired={true}
                isSearchable={true}
                selectedOption={selectedLanguageOption}
                options={languageOptions}
                onChange={handleUpdateLanguage}
            />
            {!isDisabled && availableLanguages.length > 0 && (
                <Button onClick={onClickAddLanguage}>
                    {resource.buttons.addLanguage.label}
                </Button>
            )}
            <hr />
            <>
                <>
                    <TextareaFormGroup
                        controlId={`email-template-subject-variable`}
                        automationId={`email-template-subject-variable`}
                        label={EMAIL_TEMPLATE_SUBJECT_VARIABLE_NAME}
                        ariaLabel={EMAIL_TEMPLATE_SUBJECT_VARIABLE_NAME}
                        isRequired={true}
                        onChange={handleUpdateEmailSubject}
                        value={state.languages[selectedLanguage]?.[EMAIL_TEMPLATE_SUBJECT_VARIABLE_NAME] || ""}
                        disabled={isDisabled}
                        labelWidth={labelWidth}
                        layout={LayoutTypes.COLUMN}
                    />
                    {languageValidationErrors[EMAIL_TEMPLATE_SUBJECT_VARIABLE_NAME]}
                </>
                {variables.map((variable, index) => (
                    <Fragment key={`email-template-variable-${variable.name}`}>
                        <TextareaFormGroup
                            controlId={`email-template-variable-${variable.name}`}
                            automationId={`email-template-variable-${variable.name}`}
                            label={variable.name}
                            ariaLabel={variable.name}
                            isRequired={true}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => handleChangeAtIndex(event, index)}
                            value={variable.value}
                            disabled={isDisabled}
                            labelWidth={labelWidth}
                            layout={LayoutTypes.COLUMN}
                        />
                        {languageValidationErrors[variable.name] || null}
                    </Fragment>
                ))}
            </>
            {variables.length === 0 && (
                <Alert>{resource.noVariablesMessage}</Alert>
            )}
        </ThemingFormSection>
    );
};